import { TermsDocumentType, TermsLanguage, Application } from '../../schemas/documents';
import { PaginatedResource } from '../../typings/Paginated';
import { BaseService, GenericPaginatedParameters, Unauthorized, UnexpectedError, ClientError, PartialBy } from './base-service';

interface TermsOfUseAndPrivacyVersion {
    id: number;
    version: number;
    htmlContent?: string;
    effectiveDate: string;
    createdAt?: string;
}

interface TermsOfUseAndPrivacy {
    id: number;
    documentType: TermsDocumentType;
    language: TermsLanguage;
    application: Application;
    companyId: number;
    versions: TermsOfUseAndPrivacyVersion[];
    createdAt: string;
    updatedAt: string;
}

export interface TermsOfUseAndPrivacyVersionListItem extends TermsOfUseAndPrivacyVersion {
    validUntil?: string;
    termsOfUseAndPrivacy: Pick<TermsOfUseAndPrivacy, 'id' | 'documentType' | 'language' | 'application'>;
}

export interface GetAllTermsOfUseAndPrivacyVersionsParameters extends GetTermsOfUseAndPrivacyParameters {
    status?: 'scheduled' | 'previous' | 'all';
}

export interface GetDocumentCategoriesPaginatedParameters extends GenericPaginatedParameters {
    actingBodyUnitIds?: number[];
}

export interface GetTermsOfUseAndPrivacyParameters extends GenericPaginatedParameters {
    documentType?: TermsDocumentType;
    language?: TermsLanguage;
    application?: Application;
}

export class DocumentService extends BaseService {
    async getDocuments(params: GenericPaginatedParameters): Promise<PaginatedResource<DocumentFile>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/documents${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentById(id: number): Promise<DocumentFile> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentVersions(documentId: number): Promise<{ id: number; version: number; createdAt: string; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${documentId}/versions`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentDownloadUrl(documentId: number, version: number): Promise<string> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document/${documentId}/version/${version}/download`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        const { url } = await res.json();
        return url;
    }

    async createDocument(params: Omit<BaseDocument, 'id'> & { file: File; }): Promise<DocumentFile> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', params.file, params.file.name);
        formData.append('title', params.title);
        formData.append('documentCategoryId', String(params.documentCategoryId));
        formData.append('actingBodyUnitId', String(params.actingBodyUnitId));

        const res = await fetch(`${this.centralEndpoint}/document`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateDocument(id: number, file: File): Promise<DocumentFile> {
        const token = await this.getToken();
        const formData = new FormData();
        formData.append('file', file, file.name);

        const res = await fetch(`${this.centralEndpoint}/document/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token),
            body: formData,
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentCategories(params: GetDocumentCategoriesPaginatedParameters): Promise<PaginatedResource<DocumentCategory>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-categories${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getSimplifiedDocumentCategories(params: { actingBodyUnitId?: number; }): Promise<SimplifiedDocumentCategory[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/simplified-document-categories${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentCategoryById(id: number): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createDocumentCategory(params: Omit<BaseDocumentCategory, 'id'>): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async updateDocumentCategory(params: PartialBy<Omit<BaseDocumentCategory, 'actingBodyUnitId'>, 'id'>): Promise<DocumentCategory> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(params),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async deleteDocumentCategory(id: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-category/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    async getActingBodies(): Promise<ActingBodySimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/documents/acting-bodies`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getDocumentUsers(params: { actingBodyId?: number; actingBodyUnitIds?: number[]; textFilter?: string; }): Promise<{ id: number; name: string; warName?: string | null; }[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/document-users${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getTermsOfUseAndPrivacy(params: GetTermsOfUseAndPrivacyParameters): Promise<PaginatedResource<TermsOfUseAndPrivacy>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getAllTermsOfUseAndPrivacyVersions(params: GetAllTermsOfUseAndPrivacyVersionsParameters): Promise<PaginatedResource<TermsOfUseAndPrivacyVersionListItem>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy-versions${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getTermsOfUseAndPrivacyById(id: number): Promise<TermsOfUseAndPrivacy> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async createTermsOfUseAndPrivacy(params: {
        documentType: TermsDocumentType;
        language: TermsLanguage;
        application: Application;
        effectiveDate: Date;
        htmlContent: string;
        isDuplicate?: boolean;
    }): Promise<TermsOfUseAndPrivacy> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                documentType: params.documentType,
                language: params.language,
                application: params.application,
                effectiveDate: params.effectiveDate.toISOString(),
                htmlContent: params.htmlContent,
                isDuplicate: params.isDuplicate,
            }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 201) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getCurrentTermsOfUseAndPrivacy(documentType: TermsDocumentType, language: TermsLanguage): Promise<{
        id: number;
        documentType: TermsDocumentType;
        language: TermsLanguage;
        version: number;
        effectiveDate: string;
        htmlContent: string;
    }> {
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/current${this.encodeQueryParams({ documentType, language })}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json;charset=utf-8' },
        });

        if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async acceptTermsOfUse(versionId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/terms-of-use/accept`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ versionId }),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async acceptPrivacyPolicy(versionId: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/privacy-policy/accept`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ versionId }),
        });

        if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateTermsOfUseAndPrivacy(id: number, params: { effectiveDate: Date; htmlContent: string; }): Promise<TermsOfUseAndPrivacy> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                effectiveDate: params.effectiveDate.toISOString(),
                htmlContent: params.htmlContent,
            }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getTermsOfUseAndPrivacyVersions(id: number): Promise<TermsOfUseAndPrivacyVersion[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/versions`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    // ----- Ações sobre versões agendadas -----

    async publishTermsOfUseAndPrivacyVersion(id: number, version: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}/publish`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async updateTermsOfUseAndPrivacyVersion(id: number, version: number, params: { effectiveDate: Date; htmlContent: string; documentType: TermsDocumentType; language: TermsLanguage; }): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({
                effectiveDate: params.effectiveDate.toISOString(),
                htmlContent: params.htmlContent,
                documentType: params.documentType,
                language: params.language,
            }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
    }

    async deleteTermsOfUseAndPrivacyVersion(id: number, version: number): Promise<void> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 204) {
            throw new UnexpectedError();
        }
    }

    /** Baixa o arquivo gerado pelo back; o nome vem do Content-Disposition */
    async downloadTermsOfUseAndPrivacyVersion(id: number, version: number): Promise<{ blob: Blob; fileName: string; }> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}/download`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        const disposition = res.headers.get('Content-Disposition') ?? '';
        const fileName = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? `terms_v${version}.html`;

        return { blob: await res.blob(), fileName };
    }

    // Busca o HTML de uma versão (usado em visualizar/duplicar)
    async getTermsOfUseAndPrivacyVersion(id: number, version: number): Promise<TermsOfUseAndPrivacyVersion> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}`, {
            method: 'GET',
            headers: this.getHeaders(token),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }

    async getTermsOfUseAndPrivacyAcceptedUsers(id: number, version: number, params: GenericPaginatedParameters): Promise<PaginatedResource<{
        id: number;
        name: string;
        actingBody: string | null;
        actingBodyUnit: string | null;
        status: 'accepted' | 'pending';
    }>> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/terms-of-use-and-privacy/${id}/version/${version}/accepted-users${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status == 404) {
            throw new ClientError('NotFound');
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }
        return res.json();
    }
}

export const documentService = new DocumentService();
